import { claimAndroidDeviceHandoff, registerAndroidDevice } from '@/common/apis/services/notifications/inbox';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  NATIVE_PUSH_RESULT_EVENT,
  isNativePushCapable,
  isNativePushEnabled,
  markNativePushEnabled,
  nativePushEnableUrl,
  newHandoffCode,
  rememberNativePushCapability,
  takeNativePushResult,
} from '../utils/nativePush';

export type NativePushOutcome = 'granted' | 'denied';

const CLAIM_INTERVAL_MS = 1500;
/** ~90 s: long enough for the permission dialog or a trip to the settings page. */
const CLAIM_ATTEMPTS = 60;

/**
 * The app's notification permission flow without any UI: each place that asks (doctor home,
 * patient after booking, …) draws its own banner and result sheet with its own copy.
 */
export const useNativePushPrompt = ({ onEvent }: { onEvent?: (name: string, data?: Record<string, unknown>) => void } = {}) => {
  const [canAsk, setCanAsk] = useState(false);
  const [outcome, setOutcome] = useState<NativePushOutcome | null>(null);
  const onEventRef = useRef(onEvent);
  /** Set while the app is asking; cleared once an answer arrives by either route. */
  const handoffRef = useRef<string | null>(null);
  const claimTimer = useRef<ReturnType<typeof setTimeout>>();
  onEventRef.current = onEvent;

  useEffect(() => {
    rememberNativePushCapability();
    const handle = () => {
      const result = takeNativePushResult();
      if (!result) return;
      handoffRef.current = null;
      onEventRef.current?.('push_enable_result', { granted: result.granted, token: !!result.token });
      if (!result.granted) {
        setOutcome('denied');
        // Link the phone even so: it can be turned on later in settings.
        if (result.token) registerAndroidDevice(result.token).catch(() => undefined);
        return;
      }
      if (!result.token) {
        setOutcome('denied');
        return;
      }
      registerAndroidDevice(result.token)
        .then(() => {
          markNativePushEnabled();
          setCanAsk(false);
          setOutcome('granted');
        })
        .catch(() => setOutcome('denied'));
    };
    handle();
    setCanAsk(isNativePushCapable() && !isNativePushEnabled());
    // New apps answer live over the TWA message channel (no reload); see NATIVE_PUSH_INLINE_SCRIPT.
    window.addEventListener(NATIVE_PUSH_RESULT_EVENT, handle);

    return () => {
      window.removeEventListener(NATIVE_PUSH_RESULT_EVENT, handle);
      clearTimeout(claimTimer.current);
    };
  }, []);

  // Fallback when the app couldn't use the message channel: it left the token on the server under
  // our code. The permission dialog doesn't hide the page (no visibilitychange), so poll gently
  // until an answer arrives by either route.
  const claim = useCallback((code: string, attempt: number) => {
    if (handoffRef.current !== code || attempt > CLAIM_ATTEMPTS) return;
    claimAndroidDeviceHandoff(code)
      .then(({ granted }) => {
        if (handoffRef.current !== code) return;
        handoffRef.current = null;
        onEventRef.current?.('push_enable_result', { granted, token: true, handoff: true });
        if (granted) {
          markNativePushEnabled();
          setCanAsk(false);
        }
        setOutcome(granted ? 'granted' : 'denied');
      })
      .catch(() => {
        claimTimer.current = setTimeout(() => claim(code, attempt + 1), CLAIM_INTERVAL_MS);
      });
  }, []);

  const enable = useCallback(() => {
    onEventRef.current?.('push_enable_click');
    const code = newHandoffCode();
    handoffRef.current = code;
    window.location.href = nativePushEnableUrl(window.location.pathname + window.location.search, code);
    clearTimeout(claimTimer.current);
    claimTimer.current = setTimeout(() => claim(code, 0), CLAIM_INTERVAL_MS);
  }, [claim]);

  const dismissOutcome = useCallback(() => setOutcome(null), []);

  return { canAsk, outcome, enable, dismissOutcome };
};
